import { useEffect, useRef, useState } from "react";
import { Snowflake, Droplets, Flame, Hammer, Check, Droplet } from "lucide-react";
import ScoreSubmit from "@/components/landing/games/ScoreSubmit";
import BrickTitle from "@/components/landing/BrickTitle";

const issues = [
  {
    id: "ac",
    icon: Snowflake,
    label: "AC Not Cooling",
    plan: "We arrive with calibrated manifold gauges and EPA-certified refrigerant handling. The exact fault — low charge, weak capacitor, or dirty coils — is isolated, explained in plain English with upfront parts pricing, and fixed cleanly the same visit whenever possible.",
  },
  {
    id: "leak",
    icon: Droplets,
    label: "Roof / Gutter Leak",
    plan: "We trace the water path from stain to source — flashing, shingles, or gutter pitch — then seal it properly with lead-free flashing and realign runoff so the leak stays gone through the next storm.",
  },
  {
    id: "weld",
    icon: Flame,
    label: "Broken Gate / Steel",
    plan: "Mobile welding rig comes right to your driveway. Hinges, posts, and railings are re-set, welded, and reinforced on-site — no shop minimums, no hauling your gate across town.",
  },
  {
    id: "custom",
    icon: Hammer,
    label: "Custom Wood / Part",
    plan: "Send a photo or the broken piece. We match hardwoods and finishes for built-ins and furniture, or measure, 3D model, and print a high-strength replacement for discontinued parts.",
  },
];

const TARGET = 6;

export default function Diagnostic() {
  const [selected, setSelected] = useState("ac");
  const [drops, setDrops] = useState([]);
  const [patched, setPatched] = useState(0);
  const [started, setStarted] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const idRef = useRef(0);
  const startRef = useRef(null);
  const sealed = patched >= TARGET;

  useEffect(() => {
    if (!started || sealed) return;
    const t = setInterval(() => {
      idRef.current += 1;
      const id = idRef.current;
      const left = 8 + Math.random() * 84;
      setDrops((d) => [...d, { id, left }]);
      setTimeout(() => setDrops((d) => d.filter((x) => x.id !== id)), 2900);
    }, 900);
    return () => clearInterval(t);
  }, [started, sealed]);

  const patch = (id) => {
    setDrops((d) => d.filter((x) => x.id !== id));
    setPatched((p) => {
      const next = Math.min(p + 1, TARGET);
      if (next >= TARGET && startRef.current && finalScore === null) {
        setFinalScore(Math.max(1, Math.round((Date.now() - startRef.current) / 100)));
      }
      return next;
    });
  };

  const issue = issues.find((i) => i.id === selected);

  return (
    <section id="interactive" className="py-20 max-w-4xl mx-auto px-4 sm:px-6" data-testid="diagnostic-section">
      <div className="text-center mb-10">
        <span className="text-xs font-bold uppercase tracking-widest text-[#D4691E]">Interactive Problem Solver</span>
        <h2 className="text-3xl font-bold mt-2"><BrickTitle text="What Needs Attention In Your Home?" /></h2>
        <p className="text-sm text-[#A89F94] mt-2">Tap an issue below to see exactly how we diagnose and solve it:</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E6DCD3] p-6 sm:p-8 shadow-sm">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {issues.map((i) => (
            <button
              key={i.id}
              type="button"
              onClick={() => setSelected(i.id)}
              data-testid={`diagnostic-option-${i.id}`}
              className={`flex flex-col items-center text-center p-4 rounded-xl border cursor-pointer transition-colors ${
                selected === i.id
                  ? "border-[#D4691E] bg-[#FFF3EA] text-[#A94E10]"
                  : "border-[#E6DCD3] hover:bg-[#FAF6F1] text-[#1E252B]"
              }`}
            >
              <i.icon className="w-6 h-6 mb-1" />
              <span className="text-xs font-bold">{i.label}</span>
            </button>
          ))}
        </div>

        <div className="bg-[#F5EFEB] rounded-xl p-4 sm:p-6 border border-[#E6DCD3]">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#1E252B] text-white flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h4 className="font-bold text-base text-[#1E252B]" data-testid="diagnostic-plan-title">
                {issue.label}: Diagnostic &amp; Fast Execution Plan
              </h4>
              <p className="text-sm text-[#665E56] mt-1 leading-relaxed" data-testid="diagnostic-plan-text">
                {issue.plan}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <a
                  href="#contact"
                  data-testid="diagnostic-book-btn"
                  className="px-4 py-2 rounded bg-[#D4691E] text-white text-xs font-bold hover:bg-[#A94E10] transition-colors"
                >
                  Book This Repair
                </a>
                <span className="text-xs text-[#736B63]">&bull; Clean workspace guarantee included</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mini-game: Patch the Leak */}
        <div className="mt-8 bg-[#1E252B] rounded-xl p-6 border border-[#3A4752]" data-testid="leak-game">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-bold text-base text-white">Mini-Game: Patch the Leak</h4>
              <p className="text-xs text-[#A39B92] mt-0.5">Tap {TARGET} falling drips before they hit the floor — that's how fast we stop a leak.</p>
            </div>
            <span className="text-xs font-bold text-[#FFAE70]" data-testid="leak-game-score">
              {patched} / {TARGET} patched
            </span>
          </div>

          <div className="relative h-72 sm:h-48 rounded-lg bg-[#28323A] border border-[#3A4752] overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-2 bg-[#485663]" />
            {!started && !sealed && (
              <button
                type="button"
                onClick={() => {
                  startRef.current = Date.now();
                  setStarted(true);
                }}
                data-testid="leak-game-start-btn"
                className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white bg-[#1E252B]/60 hover:bg-[#1E252B]/40 transition-colors"
              >
                Tap to Start the Drip
              </button>
            )}
            {drops.map((d) => (
              <button
                key={d.id}
                type="button"
                aria-label="Patch drip"
                data-testid={`leak-drop-${d.id}`}
                onClick={() => patch(d.id)}
                className="mc-droplet absolute text-sky-400 hover:text-sky-200"
                style={{ left: `${d.left}%` }}
              >
                <Droplet className="w-7 h-7 fill-current" />
              </button>
            ))}
            {sealed && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1E252B]/80 text-center px-4" data-testid="leak-game-win">
                <span className="font-display text-xl font-bold text-white">Leak Sealed. Floor Dry. Job Done Right.</span>
                <p className="text-xs text-[#A39B92] mt-2">That's the care your home gets on every single visit.</p>
                <a
                  href="#contact"
                  data-testid="leak-game-quote-btn"
                  className="mt-4 px-5 py-2.5 rounded bg-[#D4691E] hover:bg-[#A94E10] text-white text-xs font-bold transition-colors"
                >
                  Get Your Quote
                </a>
              </div>
            )}
          </div>
          {sealed && finalScore !== null && <ScoreSubmit game="patch-the-leak" score={finalScore} scoreLabel="time pts" />}
        </div>
      </div>
    </section>
  );
}
